import { type ConsentJurisdiction, jurisdictions, ruleTypes } from './constants/geoRules';

const ONETRUST_SCRIPT_ELEMENT_ID = 'OneTrustScript';
const ONETRUST_CDN_ORIGIN = 'https://cdn.cookielaw.org';

// The banner template OneTrust loads for the rule it decided to apply:
// `/consent/<domainScriptId>/<geoRuleId>/<language>.json`. The trailing slash is what keeps this
// from also matching the rule set file, which is `/consent/<domainScriptId>/<domainScriptId>.json`.
const appliedRuleUrlPattern = /cookielaw\.org\/consent\/[0-9a-f-]{36}\/([0-9a-f-]{36})\//i;

/**
 * A geolocation rule as OneTrust publishes it. Only the fields we read are described; the rule set
 * carries considerably more, none of it ours to depend on.
 */
export interface OneTrustGeoRule {
  Id: string;
  Name: string;
  /** A consent template; see `ruleTypes` for the ones our dashboard uses. */
  Type: string;
  /** Set on the single rule OneTrust falls back to for locations no other rule covers. */
  Global?: boolean;
  Default?: boolean;
  /** Lowercase ISO 3166-1 alpha-2 codes. */
  Countries?: string[];
  /** Lowercase subdivision codes, keyed by lowercase country code. */
  States?: Record<string, string[]>;
}

/** The rule governing the session, reduced to what reporting and the cross-checks need. */
export interface ResolvedGeoRule {
  id: string;
  name: string;
  type: string;
  /** Undefined when the rule's template is not one we recognize. */
  jurisdiction?: ConsentJurisdiction;
  /** The rule OneTrust itself applied; empty until its banner template request has landed. */
  appliedId: string;
}

/**
 * Which regime a matched geolocation rule puts the session under.
 *
 * Deliberately keyed on the rule's shape rather than its id: rule ids are per-OneTrust-environment
 * GUIDs, so a hard-coded map would silently stop resolving on any environment but production.
 *
 * The `IAB2V2` template is the only one that carries TCF, so it is unambiguously the GDPR rule.
 * A non-TCF opt-in template is the "global opt-in" regime we apply where local law requires
 * consent but not TCF. Opt-out templates split on OneTrust's catch-all flags: the rule that
 * matches everywhere no other rule did is the global opt-out default, and a geo-scoped one is a
 * US state privacy law.
 */
export function getJurisdictionForRule(rule?: OneTrustGeoRule): ConsentJurisdiction | undefined {
  switch (rule?.Type) {
    case ruleTypes.tcfV2:
      return jurisdictions.gdpr;
    case ruleTypes.gdpr:
      return jurisdictions.globalOptIn;
    case ruleTypes.cpra:
      return rule.Global || rule.Default ? jurisdictions.globalOptOut : jurisdictions.usPrivacy;
    default:
      return undefined;
  }
}

/**
 * The OneTrust domain script id, read off the tag the server renders (see server/components/
 * OneTrust.jsx). ONETRUST_ENV_KEY is a server-only env var, and this is the same value.
 */
export function getDomainScriptId(): string {
  const script = document.getElementById(ONETRUST_SCRIPT_ELEMENT_ID);

  return (script instanceof HTMLElement && script.dataset.domainScript) || '';
}

/**
 * Every geolocation rule configured in the OneTrust dashboard, with the countries and states each
 * one covers. Unauthenticated, and already in the browser cache because the OneTrust stub fetches
 * it on page load to make the same decision.
 *
 * @throws if the rule set cannot be read
 */
export async function fetchGeoRuleSet(domainScriptId: string): Promise<OneTrustGeoRule[]> {
  if (!domainScriptId) {
    return [];
  }

  const response = await fetch(
    `${ONETRUST_CDN_ORIGIN}/consent/${domainScriptId}/${domainScriptId}.json`,
  );

  if (!response.ok) {
    throw new Error(`OneTrust rule set request failed with status ${response.status}`);
  }

  const { RuleSet: ruleSet } = (await response.json()) as { RuleSet?: OneTrustGeoRule[] };

  return ruleSet || [];
}

/**
 * Picks the rule OneTrust would apply for a location, most specific first.
 *
 * State rules have to beat country rules: `us` appears in the catch-all rule's countries while
 * California lives in the CCPA rule's states, and `ca` (Canada) appears in the catch-all's
 * countries while Quebec lives in the global opt-in rule's states. Matching on country first would
 * put every Californian and every Quebecer under the wrong regime.
 *
 * @param countryCode - ISO 3166-1 alpha-2, from the Platform config
 * @param stateCode - ISO 3166-2 subdivision, from the Platform config
 */
export function matchGeoRule(
  ruleSet: OneTrustGeoRule[] = [],
  countryCode?: string,
  stateCode?: string,
): OneTrustGeoRule | undefined {
  const country = countryCode?.toLowerCase();

  if (!country) {
    return undefined;
  }

  const state = stateCode?.toLowerCase();
  const stateRule = state
    ? ruleSet.find((rule) => rule.States?.[country]?.includes(state))
    : undefined;

  return (
    stateRule ||
    ruleSet.find((rule) => rule.Countries?.includes(country)) ||
    // OneTrust marks exactly one rule as the catch-all for locations no other rule covers.
    ruleSet.find((rule) => rule.Global || rule.Default)
  );
}

/**
 * The id of the rule OneTrust itself applied, recovered from the banner template request rather
 * than from an SDK API — OneTrust does not expose the applied rule programmatically.
 *
 * Only ever a cross-check on our own resolution: the request lands after the banner SDK loads, so
 * this is empty for part of every page load.
 */
export function getAppliedGeoRuleId(): string {
  const resources = globalThis.performance?.getEntriesByType?.('resource') ?? [];

  return resources.reduce<string>(
    (found, { name }) => found || name.match(appliedRuleUrlPattern)?.[1] || '',
    '',
  );
}

/**
 * Determines which privacy regime governs the session by evaluating OneTrust's own geolocation
 * rules against the Platform's country and state.
 *
 * We resolve this ourselves rather than reading it off the SDK for two reasons: OneTrust exposes
 * no API for it, and their geolocation is cached while the Platform's is not, so deriving the
 * jurisdiction from the same location we hand OneTrust is the only way the two cannot disagree.
 */
export async function resolveGeoRule({
  countryCode,
  stateCode,
}: {
  countryCode?: string;
  stateCode?: string;
}): Promise<ResolvedGeoRule | undefined> {
  const ruleSet = await fetchGeoRuleSet(getDomainScriptId());
  const rule = matchGeoRule(ruleSet, countryCode, stateCode);

  if (!rule) {
    return undefined;
  }

  return {
    id: rule.Id,
    name: rule.Name,
    type: rule.Type,
    jurisdiction: getJurisdictionForRule(rule),
    appliedId: getAppliedGeoRuleId(),
  };
}
